export type PropertyValue = string | number | boolean;

export interface PropertyDescriptor {
  name: string;
  type: "string" | "number" | "boolean" | "enum";
  value: PropertyValue;
  options?: PropertyValue[];
  /** Required when type is "number": inclusive bounds and the granularity of value. */
  min?: number;
  max?: number;
  step?: number;
  readonly?: boolean;
}

export interface CameraStatus {
  battery?: number;
  storageRemaining?: number;
}

/** width/height are display size: the JPEG's EXIF orientation is already applied. */
export interface PreviewFrame {
  data: Blob;
  width: number;
  height: number;
}

/** width/height are display size: the JPEG's EXIF orientation is already applied. */
export interface CaptureResult {
  blob: Blob;
  width: number;
  height: number;
}

/** What a recording yields. width/height are display size, as everywhere else. */
export interface RecordResult {
  blob: Blob;
  width: number;
  height: number;
  durationSeconds: number;
  frameRate: number;
}

export const BRIDGE_ERROR_CODES = [
  "NotConnected",
  "CaptureFailed",
  "RecordFailed",
  "PreviewLost",
  "PropertyUnsupported",
  "Timeout",
  "PermissionDenied",
  "BridgeFailed",
  /** The client's own storage, not the bridge's: never sent on the wire. */
  "Storage",
] as const;

export type BridgeErrorCode = (typeof BRIDGE_ERROR_CODES)[number];

export class BridgeError extends Error {
  constructor(
    public readonly code: BridgeErrorCode,
    message: string,
    public readonly detail?: unknown,
  ) {
    super(message);
    this.name = "BridgeError";
  }
}

export interface CameraInfo {
  id: string;
  label: string;
  /** Set when the bridge found the camera but cannot serve it; connect will refuse. */
  unusable?: string;
  /** Set where the camera can record a clip; the app offers the control only then, and a
   *  camera without it refuses with RecordFailed. */
  canRecord?: boolean;
}

/** Every method but `listCameras()` needs a live session and throws `NotConnected` without
 *  one, ahead of any other check. */
export interface CameraBridge {
  listCameras(): Promise<CameraInfo[]>;
  connect(cameraId: string): Promise<void>;
  disconnect(): Promise<void>;
  getStatus(): Promise<CameraStatus>;
  listProperties(): Promise<PropertyDescriptor[]>;
  /** Answers with the whole list: one property can change another, and a bridge may
   *  quantise the value it was handed. */
  setProperty(name: string, value: PropertyValue): Promise<PropertyDescriptor[]>;
  capture(): Promise<CaptureResult>;
  /** Refused with RecordFailed by a camera that does not advertise canRecord, and by
   *  one already recording. */
  startRecording(): Promise<void>;
  stopRecording(): Promise<RecordResult>;
}

/** A bridge reached over a wire, whose live frames are JPEG until something decodes them. */
export interface RemoteCameraBridge extends CameraBridge {
  startPreview(signal?: AbortSignal): AsyncIterable<PreviewFrame>;
}
